export type DevConverterVariant =
  | 'px-to-rem-converter'
  | 'json-to-typescript'
  | 'css-to-tailwind'
  | 'cron-builder';

function roundTo(value: number, decimals = 5): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/* ------------------------------------------------------------------ */
/* px <-> rem                                                          */
/* ------------------------------------------------------------------ */

export function parseRoot(value: string): number {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return 16;
  }
  return parsed;
}

export function pxToRem(px: string, root: number): string {
  const value = Number.parseFloat(px);
  if (!Number.isFinite(value)) {
    return '';
  }
  return String(roundTo(value / root));
}

export function remToPx(rem: string, root: number): string {
  const value = Number.parseFloat(rem);
  if (!Number.isFinite(value)) {
    return '';
  }
  return String(roundTo(value * root));
}

export function convertCssPxToRem(css: string, root: number): string {
  if (!css.trim()) {
    return '';
  }
  return css.replace(/(-?\d*\.?\d+)px\b/g, (_match, numeric: string) => {
    const value = Number.parseFloat(numeric);
    if (!Number.isFinite(value)) {
      return `${numeric}px`;
    }
    if (value === 0) {
      return '0';
    }
    return `${roundTo(value / root)}rem`;
  });
}

/* ------------------------------------------------------------------ */
/* JSON -> TypeScript                                                  */
/* ------------------------------------------------------------------ */

type TsResult = { output: string; error: string | null };

function sanitizeInterfaceName(raw: string): string {
  const cleaned = raw.replace(/[^A-Za-z0-9]/g, ' ').trim();
  if (!cleaned) {
    return 'Root';
  }
  const pascal = cleaned
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
  return /^[A-Za-z]/.test(pascal) ? pascal : `Type${pascal}`;
}

function singularize(name: string): string {
  if (/ies$/.test(name)) {
    return `${name.slice(0, -3)}y`;
  }
  if (/(ses|xes|zes|ches|shes)$/.test(name)) {
    return name.slice(0, -2);
  }
  if (/s$/.test(name) && !/ss$/.test(name)) {
    return name.slice(0, -1);
  }
  return name;
}

function isSafeKey(key: string): boolean {
  return /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(key);
}

function jsonValueType(value: unknown): string {
  if (value === null) return 'null';
  if (typeof value === 'string') return 'string';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  return 'unknown';
}

export function jsonToTypeScript(input: string, rootName: string): TsResult {
  if (!input.trim()) {
    return { output: '', error: null };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(input);
  } catch (error) {
    return {
      output: '',
      error: error instanceof Error ? `Invalid JSON: ${error.message}` : 'Invalid JSON.',
    };
  }

  const rootInterfaceName = sanitizeInterfaceName(rootName);
  const interfaces: string[] = [];
  const usedNames = new Set<string>();

  function uniqueName(base: string): string {
    const name = sanitizeInterfaceName(base);
    if (!usedNames.has(name)) {
      usedNames.add(name);
      return name;
    }
    let counter = 2;
    while (usedNames.has(`${name}${counter}`)) {
      counter += 1;
    }
    const finalName = `${name}${counter}`;
    usedNames.add(finalName);
    return finalName;
  }

  function typeOfArray(values: unknown[], nameHint: string): string {
    if (values.length === 0) {
      return 'unknown[]';
    }
    const elementTypes = new Set<string>();
    const objectSamples: Record<string, unknown>[] = [];
    for (const item of values) {
      if (item !== null && typeof item === 'object' && !Array.isArray(item)) {
        objectSamples.push(item as Record<string, unknown>);
      } else if (Array.isArray(item)) {
        elementTypes.add(typeOfArray(item, singularize(nameHint)));
      } else {
        elementTypes.add(jsonValueType(item));
      }
    }
    if (objectSamples.length > 0) {
      const merged = mergeObjects(objectSamples);
      const objectType = typeOfMergedObject(merged, singularize(nameHint));
      elementTypes.add(objectType);
    }
    const union = [...elementTypes].join(' | ');
    return elementTypes.size > 1 ? `(${union})[]` : `${union}[]`;
  }

  function mergeObjects(samples: Record<string, unknown>[]): {
    fields: Map<string, Set<unknown>>;
    optional: Set<string>;
  } {
    const fields = new Map<string, Set<unknown>>();
    const optional = new Set<string>();
    for (const sample of samples) {
      const keys = Object.keys(sample);
      for (const key of keys) {
        if (!fields.has(key)) {
          fields.set(key, new Set());
        }
        fields.get(key)!.add(sample[key]);
      }
    }
    for (const key of fields.keys()) {
      const presentEverywhere = samples.every((sample) =>
        Object.prototype.hasOwnProperty.call(sample, key),
      );
      if (!presentEverywhere) {
        optional.add(key);
      }
    }
    return { fields, optional };
  }

  function typeOfMergedObject(
    merged: { fields: Map<string, Set<unknown>>; optional: Set<string> },
    nameHint: string,
  ): string {
    const interfaceName = uniqueName(nameHint);
    const lines: string[] = [`interface ${interfaceName} {`];
    for (const [key, valuesSet] of merged.fields) {
      const values = [...valuesSet];
      const memberTypes = new Set<string>();
      for (const value of values) {
        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
          const single = mergeObjects([value as Record<string, unknown>]);
          memberTypes.add(typeOfMergedObject(single, key));
        } else if (Array.isArray(value)) {
          memberTypes.add(typeOfArray(value, key));
        } else {
          memberTypes.add(jsonValueType(value));
        }
      }
      const typeStr = [...memberTypes].join(' | ') || 'unknown';
      const optionalMark = merged.optional.has(key) ? '?' : '';
      const safeKey = isSafeKey(key) ? key : JSON.stringify(key);
      lines.push(`  ${safeKey}${optionalMark}: ${typeStr};`);
    }
    lines.push('}');
    interfaces.push(lines.join('\n'));
    return interfaceName;
  }

  function typeOfObject(sample: Record<string, unknown>, nameHint: string): string {
    return typeOfMergedObject(mergeObjects([sample]), nameHint);
  }

  let rootType: string;
  if (Array.isArray(parsed)) {
    usedNames.add(rootInterfaceName);
    rootType = typeOfArray(parsed, singularize(rootInterfaceName));
    const alias = `type ${rootInterfaceName} = ${rootType};`;
    return { output: [...interfaces, alias].join('\n\n'), error: null };
  }

  if (parsed !== null && typeof parsed === 'object') {
    typeOfObject(parsed as Record<string, unknown>, rootInterfaceName);
    return { output: interfaces.join('\n\n'), error: null };
  }

  const alias = `type ${rootInterfaceName} = ${jsonValueType(parsed)};`;
  return { output: alias, error: null };
}

/* ------------------------------------------------------------------ */
/* CSS -> Tailwind                                                     */
/* ------------------------------------------------------------------ */

type TailwindResult = { classes: string; unmapped: string[] };

const SPACING_SCALE: Record<string, string> = {
  '0': '0',
  '1': 'px',
  '2': '0.5',
  '4': '1',
  '6': '1.5',
  '8': '2',
  '10': '2.5',
  '12': '3',
  '14': '3.5',
  '16': '4',
  '20': '5',
  '24': '6',
  '28': '7',
  '32': '8',
  '36': '9',
  '40': '10',
  '44': '11',
  '48': '12',
  '56': '14',
  '64': '16',
  '80': '20',
  '96': '24',
};

function spacingToken(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === '0') return '0';
  const pxMatch = trimmed.match(/^(-?\d*\.?\d+)px$/);
  if (pxMatch) {
    const negative = pxMatch[1].startsWith('-');
    const abs = pxMatch[1].replace('-', '');
    const token = SPACING_SCALE[abs];
    if (token) {
      return `${negative ? '-' : ''}${token}`;
    }
    return `[${trimmed}]`;
  }
  const remMatch = trimmed.match(/^(-?\d*\.?\d+)rem$/);
  if (remMatch) {
    const negative = remMatch[1].startsWith('-');
    const abs = Math.abs(Number.parseFloat(remMatch[1]));
    const token = SPACING_SCALE[String(abs * 16)];
    if (token) {
      return `${negative ? '-' : ''}${token}`;
    }
    return `[${trimmed}]`;
  }
  if (/^(auto|100%|full)$/.test(trimmed)) {
    return trimmed === '100%' ? 'full' : trimmed;
  }
  return `[${trimmed}]`;
}

function sizeToken(value: string): string {
  const trimmed = value.trim();
  if (trimmed === '100%') return 'full';
  if (trimmed === '100vw') return 'screen';
  if (trimmed === '100vh') return 'screen';
  if (trimmed === 'auto') return 'auto';
  const spacing = spacingToken(trimmed);
  return spacing ?? `[${trimmed}]`;
}

const FONT_SIZE_MAP: Record<string, string> = {
  '12px': 'text-xs',
  '14px': 'text-sm',
  '16px': 'text-base',
  '18px': 'text-lg',
  '20px': 'text-xl',
  '24px': 'text-2xl',
  '30px': 'text-3xl',
  '36px': 'text-4xl',
  '48px': 'text-5xl',
  '60px': 'text-6xl',
  '0.75rem': 'text-xs',
  '0.875rem': 'text-sm',
  '1rem': 'text-base',
  '1.125rem': 'text-lg',
  '1.25rem': 'text-xl',
  '1.5rem': 'text-2xl',
  '1.875rem': 'text-3xl',
  '2.25rem': 'text-4xl',
  '3rem': 'text-5xl',
};

const FONT_WEIGHT_MAP: Record<string, string> = {
  '100': 'font-thin',
  '200': 'font-extralight',
  '300': 'font-light',
  '400': 'font-normal',
  normal: 'font-normal',
  '500': 'font-medium',
  '600': 'font-semibold',
  '700': 'font-bold',
  bold: 'font-bold',
  '800': 'font-extrabold',
  '900': 'font-black',
};

const RADIUS_MAP: Record<string, string> = {
  '0': 'rounded-none',
  '2px': 'rounded-sm',
  '4px': 'rounded',
  '0.25rem': 'rounded',
  '6px': 'rounded-md',
  '8px': 'rounded-lg',
  '0.5rem': 'rounded-lg',
  '12px': 'rounded-xl',
  '16px': 'rounded-2xl',
  '24px': 'rounded-3xl',
  '9999px': 'rounded-full',
  '50%': 'rounded-full',
};

const NAMED_COLORS: Record<string, string> = {
  black: 'black',
  white: 'white',
  transparent: 'transparent',
  red: 'red-500',
  blue: 'blue-500',
  green: 'green-500',
  gray: 'gray-500',
  grey: 'gray-500',
  yellow: 'yellow-500',
  orange: 'orange-500',
  purple: 'purple-500',
  pink: 'pink-500',
  indigo: 'indigo-500',
};

function colorToken(value: string): string {
  const trimmed = value.trim().toLowerCase();
  if (NAMED_COLORS[trimmed]) {
    return NAMED_COLORS[trimmed];
  }
  if (/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/.test(trimmed)) {
    return `[${trimmed}]`;
  }
  if (/^(rgb|rgba|hsl|hsla)\(/.test(trimmed)) {
    return `[${trimmed.replace(/\s+/g, '')}]`;
  }
  return `[${trimmed}]`;
}

const DISPLAY_MAP: Record<string, string> = {
  block: 'block',
  'inline-block': 'inline-block',
  inline: 'inline',
  flex: 'flex',
  'inline-flex': 'inline-flex',
  grid: 'grid',
  'inline-grid': 'inline-grid',
  none: 'hidden',
  contents: 'contents',
  table: 'table',
};

const JUSTIFY_MAP: Record<string, string> = {
  'flex-start': 'justify-start',
  start: 'justify-start',
  'flex-end': 'justify-end',
  end: 'justify-end',
  center: 'justify-center',
  'space-between': 'justify-between',
  'space-around': 'justify-around',
  'space-evenly': 'justify-evenly',
};

const ALIGN_MAP: Record<string, string> = {
  'flex-start': 'items-start',
  start: 'items-start',
  'flex-end': 'items-end',
  end: 'items-end',
  center: 'items-center',
  baseline: 'items-baseline',
  stretch: 'items-stretch',
};

const TEXT_ALIGN_MAP: Record<string, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
  justify: 'text-justify',
};

const POSITION_MAP: Record<string, string> = {
  static: 'static',
  relative: 'relative',
  absolute: 'absolute',
  fixed: 'fixed',
  sticky: 'sticky',
};

function mapDeclaration(prop: string, value: string): string | null {
  const p = prop.trim().toLowerCase();
  const v = value.trim().toLowerCase();
  switch (p) {
    case 'margin':
      return `m-${spacingToken(v)}`;
    case 'margin-top':
      return `mt-${spacingToken(v)}`;
    case 'margin-right':
      return `mr-${spacingToken(v)}`;
    case 'margin-bottom':
      return `mb-${spacingToken(v)}`;
    case 'margin-left':
      return `ml-${spacingToken(v)}`;
    case 'padding':
      return `p-${spacingToken(v)}`;
    case 'padding-top':
      return `pt-${spacingToken(v)}`;
    case 'padding-right':
      return `pr-${spacingToken(v)}`;
    case 'padding-bottom':
      return `pb-${spacingToken(v)}`;
    case 'padding-left':
      return `pl-${spacingToken(v)}`;
    case 'gap':
      return `gap-${spacingToken(v)}`;
    case 'width':
      return `w-${sizeToken(v)}`;
    case 'height':
      return `h-${sizeToken(v)}`;
    case 'min-width':
      return `min-w-${sizeToken(v)}`;
    case 'max-width':
      return `max-w-${sizeToken(v)}`;
    case 'min-height':
      return `min-h-${sizeToken(v)}`;
    case 'max-height':
      return `max-h-${sizeToken(v)}`;
    case 'color':
      return `text-${colorToken(v)}`;
    case 'background':
    case 'background-color':
      return `bg-${colorToken(v)}`;
    case 'border-color':
      return `border-${colorToken(v)}`;
    case 'display':
      return DISPLAY_MAP[v] ?? null;
    case 'flex-direction':
      return v === 'column'
        ? 'flex-col'
        : v === 'row'
          ? 'flex-row'
          : v === 'column-reverse'
            ? 'flex-col-reverse'
            : v === 'row-reverse'
              ? 'flex-row-reverse'
              : null;
    case 'flex-wrap':
      return v === 'wrap' ? 'flex-wrap' : v === 'nowrap' ? 'flex-nowrap' : null;
    case 'justify-content':
      return JUSTIFY_MAP[v] ?? null;
    case 'align-items':
      return ALIGN_MAP[v] ?? null;
    case 'font-size':
      return FONT_SIZE_MAP[v] ?? `text-[${v}]`;
    case 'font-weight':
      return FONT_WEIGHT_MAP[v] ?? null;
    case 'font-style':
      return v === 'italic' ? 'italic' : v === 'normal' ? 'not-italic' : null;
    case 'text-align':
      return TEXT_ALIGN_MAP[v] ?? null;
    case 'text-transform':
      return v === 'uppercase'
        ? 'uppercase'
        : v === 'lowercase'
          ? 'lowercase'
          : v === 'capitalize'
            ? 'capitalize'
            : v === 'none'
              ? 'normal-case'
              : null;
    case 'text-decoration':
    case 'text-decoration-line':
      return v === 'underline'
        ? 'underline'
        : v === 'line-through'
          ? 'line-through'
          : v === 'none'
            ? 'no-underline'
            : null;
    case 'border-radius':
      return RADIUS_MAP[v] ?? `rounded-[${v}]`;
    case 'opacity': {
      const num = Number.parseFloat(v);
      return Number.isFinite(num) ? `opacity-${Math.round(num * 100)}` : null;
    }
    case 'position':
      return POSITION_MAP[v] ?? null;
    case 'overflow':
      return v === 'hidden'
        ? 'overflow-hidden'
        : v === 'auto'
          ? 'overflow-auto'
          : v === 'scroll'
            ? 'overflow-scroll'
            : v === 'visible'
              ? 'overflow-visible'
              : null;
    case 'cursor':
      return `cursor-${v}`;
    case 'z-index':
      return /^\d+$/.test(v) ? `z-${v}` : `z-[${v}]`;
    default:
      return null;
  }
}

export function cssToTailwind(input: string): TailwindResult {
  const cleaned = input.replace(/\/\*[\s\S]*?\*\//g, '').replace(/[{}]/g, '\n');
  const declarations = cleaned
    .split(/[;\n]/)
    .map((line) => line.trim())
    .filter(Boolean);

  const classes: string[] = [];
  const unmapped: string[] = [];

  for (const decl of declarations) {
    const colonIndex = decl.indexOf(':');
    if (colonIndex === -1) {
      continue;
    }
    const prop = decl.slice(0, colonIndex).trim();
    const value = decl.slice(colonIndex + 1).trim();
    if (!prop || !value) {
      continue;
    }
    const mapped = mapDeclaration(prop, value);
    if (mapped) {
      classes.push(mapped);
    } else {
      unmapped.push(`${prop}: ${value}`);
    }
  }

  return { classes: classes.join(' '), unmapped };
}

/* ------------------------------------------------------------------ */
/* Cron builder + explainer                                           */
/* ------------------------------------------------------------------ */

export type CronFields = {
  minute: string;
  hour: string;
  dayOfMonth: string;
  month: string;
  dayOfWeek: string;
};

export const CRON_PRESETS: { label: string; value: CronFields }[] = [
  {
    label: 'Every minute',
    value: { minute: '*', hour: '*', dayOfMonth: '*', month: '*', dayOfWeek: '*' },
  },
  {
    label: 'Every hour',
    value: { minute: '0', hour: '*', dayOfMonth: '*', month: '*', dayOfWeek: '*' },
  },
  {
    label: 'Every day at midnight',
    value: { minute: '0', hour: '0', dayOfMonth: '*', month: '*', dayOfWeek: '*' },
  },
  {
    label: 'Every Monday at 9am',
    value: { minute: '0', hour: '9', dayOfMonth: '*', month: '*', dayOfWeek: '1' },
  },
  {
    label: 'First of month at midnight',
    value: { minute: '0', hour: '0', dayOfMonth: '1', month: '*', dayOfWeek: '*' },
  },
];

export function buildCron(fields: CronFields): string {
  const part = (value: string) => {
    const trimmed = value.trim();
    return trimmed === '' ? '*' : trimmed;
  };
  return [
    part(fields.minute),
    part(fields.hour),
    part(fields.dayOfMonth),
    part(fields.month),
    part(fields.dayOfWeek),
  ].join(' ');
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const MONTH_ALIASES: Record<string, number> = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};

const DAY_ALIASES: Record<string, number> = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
};

function describeField(
  field: string,
  options: {
    singular: string;
    plural: string;
    min: number;
    max: number;
    formatter?: (value: number) => string;
    aliases?: Record<string, number>;
  },
): string {
  const { singular, plural, min, max, formatter, aliases } = options;
  const fmt = formatter ?? ((v: number) => String(v));
  const resolve = (token: string): number | null => {
    const lower = token.toLowerCase();
    if (aliases && lower in aliases) {
      return aliases[lower];
    }
    const num = Number.parseInt(token, 10);
    return Number.isFinite(num) ? num : null;
  };

  const trimmed = field.trim();
  if (trimmed === '*' || trimmed === '?') {
    return `every ${singular}`;
  }

  const parts = trimmed.split(',');
  const descriptions = parts.map((raw) => {
    const stepMatch = raw.match(/^(.+)\/(\d+)$/);
    if (stepMatch) {
      const base = stepMatch[1];
      const step = stepMatch[2];
      if (base === '*') {
        return `every ${step} ${plural}`;
      }
      const rangeMatch = base.match(/^(\S+)-(\S+)$/);
      if (rangeMatch) {
        const from = resolve(rangeMatch[1]);
        const to = resolve(rangeMatch[2]);
        if (from !== null && to !== null) {
          return `every ${step} ${plural} from ${fmt(from)} to ${fmt(to)}`;
        }
      }
      const single = resolve(base);
      if (single !== null) {
        return `every ${step} ${plural} starting at ${fmt(single)}`;
      }
      return `every ${step} ${plural}`;
    }
    const rangeMatch = raw.match(/^(\S+)-(\S+)$/);
    if (rangeMatch) {
      const from = resolve(rangeMatch[1]);
      const to = resolve(rangeMatch[2]);
      if (from !== null && to !== null) {
        return `${fmt(from)} through ${fmt(to)}`;
      }
    }
    const single = resolve(raw);
    if (single !== null && single >= min && single <= max) {
      return fmt(single);
    }
    return raw;
  });

  const joined = descriptions.join(', ');
  if (descriptions.every((desc) => desc.startsWith('every'))) {
    return joined;
  }
  return `${singular} ${joined}`;
}

export function explainCron(expression: string): { summary: string; error: string | null } {
  const tokens = expression.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) {
    return { summary: '', error: null };
  }
  if (tokens.length !== 5) {
    return {
      summary: '',
      error: `A cron expression needs 5 fields (got ${tokens.length}): minute hour day-of-month month day-of-week.`,
    };
  }

  const [minute, hour, dayOfMonth, month, dayOfWeek] = tokens;

  const minuteDesc = describeField(minute, {
    singular: 'minute',
    plural: 'minutes',
    min: 0,
    max: 59,
  });
  const hourDesc = describeField(hour, {
    singular: 'hour',
    plural: 'hours',
    min: 0,
    max: 23,
  });
  const domDesc = describeField(dayOfMonth, {
    singular: 'day-of-month',
    plural: 'days',
    min: 1,
    max: 31,
  });
  const monthDesc = describeField(month, {
    singular: 'month',
    plural: 'months',
    min: 1,
    max: 12,
    aliases: MONTH_ALIASES,
    formatter: (v) => MONTH_NAMES[v - 1] ?? String(v),
  });
  const dowDesc = describeField(dayOfWeek, {
    singular: 'day-of-week',
    plural: 'days',
    min: 0,
    max: 7,
    aliases: DAY_ALIASES,
    formatter: (v) => DAY_NAMES[v % 7] ?? String(v),
  });

  // Simple time phrasing for the common case of a single minute + single hour.
  let timePhrase = `At ${minuteDesc} past ${hourDesc}`;
  const singleMinute = /^\d+$/.test(minute.trim());
  const singleHour = /^\d+$/.test(hour.trim());
  if (singleMinute && singleHour) {
    const hh = Number.parseInt(hour, 10);
    const mm = Number.parseInt(minute, 10);
    if (hh <= 23 && mm <= 59) {
      const pad = (n: number) => String(n).padStart(2, '0');
      timePhrase = `At ${pad(hh)}:${pad(mm)}`;
    }
  } else if (minute.trim() === '*' && hour.trim() === '*') {
    timePhrase = 'Every minute';
  } else if (minute.trim() === '*') {
    timePhrase = `Every minute during ${hourDesc}`;
  } else {
    timePhrase = `At ${minuteDesc}, ${hourDesc}`;
  }

  const dateParts: string[] = [];
  if (dayOfMonth.trim() !== '*') {
    dateParts.push(`on ${domDesc}`);
  }
  if (month.trim() !== '*') {
    dateParts.push(`in ${monthDesc}`);
  }
  if (dayOfWeek.trim() !== '*') {
    dateParts.push(`on ${dowDesc}`);
  }

  const summary = [timePhrase, ...dateParts].join(' ') + '.';
  return { summary, error: null };
}
